import { NumberKeyState, VBID, MButtonState, VideoMode } from '../types';
import { timeUtils } from './timeManager';

export const formatTimecode = (seconds: number, frameOverride?: number | null): string => {
  if (!timeUtils.isValidVideoTime(seconds)) {
    return '00:00:00 00';
  }
  
  // 如果有 frameOverride，使用自定義幀數
  if (frameOverride !== undefined && frameOverride !== null) {
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const pad = (num: number): string => num.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frameOverride)}`;
  }
  
  return timeUtils.toTimecode(seconds);
};

export const convertNumberKeysToVBIDs = (
  numberKeys: Record<string, NumberKeyState>,
  mode: string,
  mButtonState?: MButtonState
): VBID[] => {
  const vbids: VBID[] = [];
  
  Object.entries(numberKeys).forEach(([key, state]) => {
    // 🔧 修復：只要 isActive 為 true，就轉換為 VBID，確保所有活躍的重點數都被保存
    if (state.isActive) {
      // 🔧 修復：如果沒有 timestamp，使用預設值
      const timestamp = state.timestamp || 0;
      
      // 🔧 修復：檢查 state.timecode 是否為有效的時間碼格式
      let timecode: string;
      if (state.timecode && 
          state.timecode.trim() !== '' && 
          state.timecode !== '00:00:00 00' && 
          /^\d{2}:\d{2}:\d{2} \d{2}$/.test(state.timecode)) {
        // 使用有效的 state.timecode
        timecode = state.timecode;
        console.log(`✅ 使用有效的 state.timecode: ${timecode}`);
      } else {
        // 從 timestamp 生成時間碼
        timecode = formatTimecode(timestamp, state.frame) || '00:00:00 00';
        console.log(`🔧 從 timestamp 生成時間碼: ${timestamp} → ${timecode}`);
      }
      
      vbids.push({
        keyNo: Number(key),
        timecode,
        note: state.note || '',
        screenshot: state.screenshot || '',
        flag: 'on',
        privacy: 'Private' as const,
        addedDate: new Date().toISOString(),
        playMode: mButtonState,
        mediaMode: mode as VideoMode,
        timestamp: timestamp, // 🔧 確保包含 timestamp
        isActive: true, // 🔧 修復：轉換為 VBID 的重點都應該是活躍的
        path: state.path || '' // 🚨 關鍵修復：包含 path 欄位，用於數據完整性驗證
      });
      
      console.log(`🔧 convertNumberKeysToVBIDs: 轉換數字鍵 ${key}, isActive: ${state.isActive}, Note: "${state.note}", timecode: "${timecode}", timestamp: ${timestamp}`);
    } else {
      console.log(`🔧 convertNumberKeysToVBIDs: 跳過非活躍數字鍵 ${key}, isActive: ${state.isActive}`);
    }
  });
  
  console.log(`🔧 convertNumberKeysToVBIDs: 總共轉換 ${vbids.length} 個 VBID`);
  return vbids;
};

export const filterDuplicateVBIDs = (vbids: VBID[]): VBID[] => {
  const uniqueVBIDs = new Map();
  
  vbids.forEach(vbid => {
    // 使用 keyNo 而不是 timecode 來判斷唯一性
    // 因為不同的檢查點可能有相同的 timecode，但 keyNo 是唯一的
    const key = vbid.keyNo ? vbid.keyNo.toString() : vbid.timecode;
    if (!uniqueVBIDs.has(key)) {
      uniqueVBIDs.set(key, vbid);
    }
  });
  
  return Array.from(uniqueVBIDs.values());
};

export const createVBID = (
  keyNo: number,
  timecode: string,
  note: string,
  screenshot: string,
  mode: string,
  mButtonState?: MButtonState
): VBID => {
  return {
    keyNo,
    timecode,
    note,
    screenshot,
    flag: 'on',
    privacy: 'Private' as const,
    addedDate: new Date().toISOString(),
    playMode: mButtonState,
    mediaMode: mode as VideoMode
  };
}; 